/**
 * Visual Builder — the analysis builder.
 *
 * The state of this page IS an AnalysisSpec. There is no second model: the
 * controls edit the JSON object that gets POSTed, the debug panel prints that
 * same object, and loading a spec is `setSpec(JSON.parse(text))`. That is what
 * makes the round trip a fact rather than a feature — forward and back are the
 * same value, not two representations kept in step.
 *
 * Everything offered here comes from `describe(subject)`: the properties, their
 * operators and controls, the measures, the events that can follow a subject,
 * the actors, the time units, the drawings and what each drawing needs. This
 * file contains no table, no column, no unit conversion and no frame rate. If a
 * sixteenth property is authored in the catalog tomorrow it appears here with
 * no change to this file.
 *
 * The one idea worth pointing at: a sequence condition means different things
 * in different slots. In `where` it removes subjects; inside a rate measure it
 * labels them. The button on a sequence row moves the identical object between
 * the two and changes nothing else — which is the whole of the distinction,
 * made visible.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ApiError, type Session } from '../lib/api'
import { BreakdownRow, ConditionRow, MeasureRow, SequenceRow } from './visual-builder/controls'
import { show } from './visual-builder/format'
import { Matrix, grid, orient } from './visual-builder/matrix'
import type { Property, SubjectCatalog, Visualization } from './visual-builder/catalog'
import {
  axisProperty, measureBy, optional, propertyBy, required, unmet,
} from './visual-builder/catalog'
import ExamplesModal from './visual-builder/ExamplesModal'
import { assumed, dims, halfOf, shapes, unmeasured } from './visual-builder/geometry'
import { draw } from './visual-builder/pitch'
import type { HitTest, PitchCell } from './visual-builder/pitch'
import { questionFor } from './visual-builder/question'
import type { AnalysisResult, PitchDims } from './visual-builder/run'
import { analyse, describe, subjects, values as fetchValues } from './visual-builder/run'
import type {
  AnalysisSpec, Breakdown, Comparison, Condition, Measure, SequenceCondition,
} from './visual-builder/spec'
import {
  conditions, isComparison, isSequence, sameSpec, serialize, slug, stepsIn,
  unsupported, withBreakdowns, withConditions, withMeasures,
} from './visual-builder/spec'
import './admin.css'
import './visual-builder.css'

const message = (e: unknown): string =>
  e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e)

/** A new, runnable-once-pinned spec for a subject. Nothing invented. */
function fresh(cat: SubjectCatalog): AnalysisSpec {
  const fn = cat.subject.default_measures?.[0] ?? cat.measures[0]?.name ?? ''
  const plural = cat.subject.plural
  const spec: AnalysisSpec = {
    version: '1.0',
    catalog_version: cat.catalog_version,
    subject: { type: cat.subject.name },
    measures: [{
      id: slug(plural), fn,
      label: plural.charAt(0).toUpperCase() + plural.slice(1),
    }],
    visualize: { as: cat.visualizations.find((v) => v.requires.length === 0)?.name ?? 'table' },
  }
  // The catalog says which properties must be pinned; they start as empty rows
  // rather than as a guess at which match the analyst meant.
  const pins = required(cat).map((p): Condition => ({
    property: p.name, op: p.operators[0]?.name ?? '',
  }))
  return pins.length ? withConditions(spec, pins) : spec
}

/** The values chosen for the properties the catalog marks required. */
function pinned(spec: AnalysisSpec, cat: SubjectCatalog): string[] {
  const out: string[] = []
  for (const c of conditions(spec)) {
    if (!isComparison(c)) continue
    if (!propertyBy(cat, c.property)?.required) continue
    if (Array.isArray(c.value)) out.push(...c.value.map(String))
    else if (c.value !== undefined && c.value !== null) out.push(String(c.value))
  }
  return out
}

export default function VisualBuilder({ session, embedded }: {
  session: Session; embedded?: boolean
}) {
  const isStaff = session.user.is_staff || session.user.is_platform_admin
  const [cat, setCat] = useState<SubjectCatalog | null>(null)
  const [names, setNames] = useState<{ name: string; label: string }[]>([])
  const [spec, setSpec] = useState<AnalysisSpec | null>(null)
  const [boot, setBoot] = useState<string | null>(null)

  const [vals, setVals] = useState<Record<string, unknown[]>>({})
  const [busy, setBusy] = useState<Record<string, boolean>>({})
  /**
   * Measured pitch geometry per match, from the match list itself.
   *
   * It arrives with the list rather than with a result so the picker can say
   * that a selection mixes pitch sizes *before* a query runs — by the time the
   * numbers are on screen the analyst has already read them as comparable.
   */
  const [pitch, setPitch] = useState<Record<string, PitchDims>>({})

  const [result, setResult] = useState<AnalysisResult | null>(null)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [draft, setDraft] = useState('')
  const [loadNote, setLoadNote] = useState<string[]>([])
  const [loaded, setLoaded] = useState<AnalysisSpec | null>(null)

  // ------------------------------------------------------------- catalog

  useEffect(() => {
    if (!isStaff) return
    let live = true
    subjects()
      .then((root) => {
        if (!live) return
        setNames(root.subjects.map((s) => ({ name: s.name, label: s.label })))
        const first = root.subjects[0]
        if (!first) throw new Error('The catalog offers no subjects.')
        return describe(first.name)
      })
      .then((c) => {
        if (!live || !c) return
        setCat(c)
        setSpec(fresh(c))
      })
      .catch((e) => { if (live) setBoot(message(e)) })
    return () => { live = false }
  }, [isStaff])

  const pickSubject = useCallback((name: string) => {
    setBoot(null)
    describe(name)
      .then((c) => {
        setCat(c); setSpec(fresh(c)); setResult(null); setLoaded(null)
        // A value list is only meaningful for the subject it was measured on.
        asked.current.clear(); setVals({}); setBusy({}); setPitch({})
      })
      .catch((e) => setBoot(message(e)))
  }, [])

  // -------------------------------------------------------------- values

  /** Keys already requested, so a re-render never re-asks for the same list. */
  const asked = useRef(new Set<string>())
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])

  const games = useMemo(
    () => (cat && spec ? pinned(spec, cat) : []),
    [cat, spec],
  )

  /** Required properties are pinned before anything else, so nothing scopes them. */
  const keyFor = useCallback(
    (p: Property): string => (p.required ? p.name : `${p.name}|${games.join(',')}`),
    [games],
  )

  const wanted = useMemo((): Property[] => {
    if (!cat || !spec) return []
    const out = new Map<string, Property>()
    for (const p of required(cat)) if (p.has_values) out.set(p.name, p)
    for (const c of conditions(spec)) {
      if (!isComparison(c)) continue
      const p = propertyBy(cat, c.property)
      if (p?.has_values) out.set(p.name, p)
    }
    return [...out.values()]
  }, [cat, spec])

  /**
   * Fetch the value lists the visible controls need.
   *
   * `asked` is a ref, not state, and the guard is "is the component still
   * mounted" rather than "is this effect run still current". Both matter: if
   * the dedupe lived in `vals`/`busy` those would have to be dependencies, and
   * this effect's own `setBusy` would then tear itself down and orphan the
   * request it had just started — the list would load forever.
   */
  useEffect(() => {
    for (const p of wanted) {
      const key = keyFor(p)
      if (asked.current.has(key)) continue
      asked.current.add(key)
      setBusy((b) => ({ ...b, [key]: true }))
      fetchValues(p.name, p.required ? [] : games)
        .then((r) => {
          if (!mounted.current) return
          setVals((v) => ({ ...v, [key]: r.values }))
          // Only the match list carries geometry; for anything else it is absent.
          if (r.pitch) setPitch(r.pitch)
        })
        .catch(() => { if (mounted.current) setVals((v) => ({ ...v, [key]: [] })) })
        .finally(() => { if (mounted.current) setBusy((b) => ({ ...b, [key]: false })) })
    }
  }, [wanted, keyFor, games])

  /**
   * What the current selection is, geometrically. Derived, never authored.
   *
   * `mixed` having more than one entry is exactly the condition under which a
   * cross-match spatial aggregate is not comparable — this build reports raw
   * per-pitch metres and does not normalise coordinates to a common pitch, so
   * the honest move is to say so rather than to draw one pitch and imply the
   * cells line up.
   */
  const geometry = useMemo(() => ({
    mixed: shapes(pitch, games),
    guessed: assumed(pitch, games),
    // Only meaningful once the list has loaded; before that everything is unknown.
    unknown: Object.keys(pitch).length ? unmeasured(pitch, games) : [],
  }), [pitch, games])

  // --------------------------------------------------------------- slots

  // Memoized (not for speed): both feed the `sequences` useMemo below, and a
  // fresh array each render would make that memo recompute on every render.
  const filters = useMemo(() => (spec ? conditions(spec) : []), [spec])
  const measures = useMemo(() => spec?.measures ?? [], [spec])
  const breakdowns = spec?.breakdowns ?? []
  const steps = spec ? stepsIn(spec) : []

  const setFilters = (list: Condition[]) => setSpec((s) => (s ? withConditions(s, list) : s))
  const setMeasures = (list: Measure[]) => setSpec((s) => (s ? withMeasures(s, list) : s))
  const setBreaks = (list: Breakdown[]) => setSpec((s) => (s ? withBreakdowns(s, list) : s))

  const replaceFilter = (i: number, c: Condition) =>
    setFilters(filters.map((old, n) => (n === i ? c : old)))
  const dropFilter = (i: number) => setFilters(filters.filter((_, n) => n !== i))

  /** Sequences live in two slots; both are edited through the same rows. */
  const sequences = useMemo(() => {
    const out: Array<{
      step: SequenceCondition['sequence']['steps'][number]
      slot: 'filter' | 'measure'
      at: number
    }> = []
    filters.forEach((c, i) => {
      if (isSequence(c)) {
        const s = c.sequence.steps[0]
        if (s) out.push({ step: s, slot: 'filter', at: i })
      }
    })
    measures.forEach((m, i) => {
      if (m.of && isSequence(m.of)) {
        const s = m.of.sequence.steps[0]
        if (s) out.push({ step: s, slot: 'measure', at: i })
      }
    })
    return out
  }, [filters, measures])

  const rateFn = cat?.measures.find((m) => m.operand === 'condition')

  const addFilter = () => {
    if (!cat) return
    const p = optional(cat)[0]
    if (!p) return
    setFilters([...filters, { property: p.name, op: p.operators[0]?.name ?? '' }])
  }

  const addSequence = () => {
    if (!cat) return
    const ev = cat.events[0]
    const unit = cat.time_units.find((u) => u.default) ?? cat.time_units[0]
    if (!ev || !unit) return
    const id = slug(ev.name, steps.map((s) => s.id))
    setFilters([...filters, {
      sequence: {
        steps: [{
          id, event: ev.name,
          // A window has to start somewhere; five is a starting position for a
          // control, not a claim about football.
          within: { value: 5, unit: unit.label },
          by: cat.sequence_actors.find((a) => a.default)?.name ?? cat.sequence_actors[0]?.name,
        }],
      },
    }])
  }

  const editStep = (
    s: { step: SequenceCondition['sequence']['steps'][number]; slot: string; at: number },
    next: SequenceCondition['sequence']['steps'][number],
  ) => {
    // An id nobody typed follows the event it was made from, so the debug
    // panel never shows a step called "box_entry" sitting on a pass.
    const step = { ...next }
    if (step.event !== s.step.event && step.id === slug(s.step.event)) {
      step.id = slug(step.event, steps.filter((k) => k.id !== s.step.id).map((k) => k.id))
    }
    const cond: SequenceCondition = { sequence: { steps: [step] } }
    if (s.slot === 'filter') replaceFilter(s.at, cond)
    else setMeasures(measures.map((m, i) => (i === s.at ? { ...m, of: cond } : m)))
  }

  /**
   * The same predicate, moved between slots.
   *
   * Filtering asks "which subjects were followed by this?"; measuring asks
   * "what share of them were?". Nothing about the condition changes — only
   * where it sits — which is exactly what the spec says the slot decides.
   */
  const moveStep = (s: { slot: string; at: number }) => {
    if (!cat || !spec) return
    if (s.slot === 'filter') {
      const cond = filters[s.at]
      if (!cond || !isSequence(cond) || !rateFn) return
      const step = cond.sequence.steps[0]
      const ev = cat.events.find((e) => e.name === step?.event)
      const id = slug(`${step?.event ?? 'sequence'}_rate`, measures.map((m) => m.id))
      const next = withConditions(spec, filters.filter((_, n) => n !== s.at))
      setSpec(withMeasures(next, [...measures, {
        id, fn: rateFn.name,
        label: `${ev?.label ?? step?.event ?? 'Followed'} rate`,
        of: cond,
      }]))
    } else {
      const m = measures[s.at]
      if (!m || !m.of || !isSequence(m.of)) return
      const next = withMeasures(spec, measures.filter((_, n) => n !== s.at))
      setSpec(withConditions(next, [...filters, m.of]))
    }
  }

  const addMeasure = () => {
    if (!cat) return
    const fn = cat.measures.find((m) => m.operand === 'none') ?? cat.measures[0]
    if (!fn) return
    setMeasures([...measures, {
      id: slug(fn.label, measures.map((m) => m.id)), fn: fn.name,
    }])
  }

  /** Every distinct floor the analyst has set, so the panel can say which wins. */
  const floors = [...new Set(breakdowns.map((b) => b.min_subjects).filter(
    (n): n is number => typeof n === 'number' && n > 0,
  ))]

  const addBreakdown = () => {
    if (!cat) return
    const p = cat.properties[0]
    if (!p) return
    setBreaks([...breakdowns, { property: p.name }])
  }

  // ----------------------------------------------------------------- run

  const run = () => {
    if (!spec) return
    setRunning(true); setError(null)
    analyse(spec)
      .then((r) => { setResult(r); setRunning(false) })
      .catch((e) => { setError(message(e)); setRunning(false) })
  }

  // -------------------------------------------------------------- rounds

  const load = () => {
    let parsed: AnalysisSpec
    try {
      parsed = JSON.parse(draft) as AnalysisSpec
    } catch (e) {
      setLoadNote([`Not JSON: ${message(e)}`]); return
    }
    const why = unsupported(parsed)
    if (why.length) { setLoadNote(why); return }
    if (cat && parsed.subject.type !== cat.subject.name) {
      describe(parsed.subject.type)
        .then((c) => { setCat(c); setSpec(parsed); setLoaded(parsed); setLoadNote([]) })
        .catch((e) => setLoadNote([message(e)]))
      return
    }
    setSpec(parsed); setLoaded(parsed); setLoadNote([]); setResult(null)
  }

  const text = spec ? serialize(spec) : ''

  // Embedded as the Visual Builder tab of the Insights Lab, the shell
  // already renders the title and sub — repeat neither.
  const head = embedded ? null : <h1 className="page-title">Visual Builder</h1>
  if (!isStaff) {
    return (
      <>
        {head}
        <p className="page-sub">
          Priori staff only. Your club's analysis is under Insights.
        </p>
      </>
    )
  }
  if (boot) {
    return (
      <>
        {head}
        <div className="panel"><p className="adm-msg adm-msg--err">{boot}</p></div>
      </>
    )
  }
  if (!cat || !spec) {
    return (
      <>
        {head}
        <div className="panel"><p className="adm-note">Loading the catalog…</p></div>
      </>
    )
  }

  const breakdownProps = breakdowns.map((b) => b.property ?? '')
  const drawing = cat.visualizations.find((v) => v.name === spec.visualize?.as)

  return (
    <>
      {head}
      {!embedded && (
        <p className="page-sub">
          Build an analysis by choosing. The question you are asking is written
          below it, in the form the engine actually runs.
        </p>
      )}

      <div className="panel">
        <div className="adm-row vb-row">
          <span className="vb-fixed">Analyse</span>
          <select
            className="adm-select" aria-label="Subject"
            value={cat.subject.name} onChange={(e) => pickSubject(e.target.value)}
          >
            {names.map((s) => <option key={s.name} value={s.name}>{s.label}</option>)}
          </select>
          <span className="adm-note">{cat.subject.description}</span>
        </div>
        <div className="adm-row vb-row">
          <input
            className="adm-input vb-title" type="text" aria-label="Title"
            placeholder="Title" value={spec.title ?? ''}
            onChange={(e) => setSpec({ ...spec, title: e.target.value || undefined })}
          />
          <input
            className="adm-input vb-title" type="text" aria-label="Question"
            placeholder="The question, in words" value={spec.question ?? ''}
            onChange={(e) => setSpec({ ...spec, question: e.target.value || undefined })}
          />
        </div>
      </div>

      <div className="panel">
        <h2 className="adm-primary">Where</h2>
        {filters.map((c, i) => {
          if (!isComparison(c)) return null
          const p = propertyBy(cat, c.property)
          const key = p ? keyFor(p) : ''
          return (
            <ConditionRow
              key={`f${i}`} cat={cat} condition={c as Comparison}
              choices={p?.required ? [p] : optional(cat)}
              values={vals[key]} loading={Boolean(busy[key])}
              onChange={(next) => replaceFilter(i, next)}
              onRemove={p?.required ? undefined : () => dropFilter(i)}
            />
          )
        })}
        <div className="adm-actions">
          <button type="button" className="adm-btn" onClick={addFilter}>Add filter</button>
          <button type="button" className="adm-btn" onClick={addSequence}>
            Add “and then…”
          </button>
        </div>
        {games.length === 0 ? (
          <p className="adm-note">
            Pick at least one {required(cat)[0]?.label.toLowerCase() ?? 'match'} —
            value lists and results both depend on it.
          </p>
        ) : null}
        {/*
          What the selected matches were actually played on. This sits at the
          picker because it is a property of the selection, not of a result:
          once numbers are on screen an analyst has already read them as
          comparable, and the moment to say otherwise has passed.
        */}
        {geometry.mixed.length > 1 ? (
          <p className="adm-warn" role="status">
            These matches were played on different sized pitches —{' '}
            {geometry.mixed.map((sh) => `${dims(sh.dims)} (${sh.games.join(', ')})`).join('; ')}.
            Coordinates are raw metres from each pitch&apos;s own centre spot, so the
            same pitch-position bin is a different distance from goal in each, and a
            breakdown by position sums cells that do not mean the same thing.
            Totals and rates that ignore position are unaffected.
          </p>
        ) : null}
        {geometry.guessed.length ? (
          <p className="adm-warn" role="status">
            Pitch dimensions are assumed, not measured, for{' '}
            {geometry.guessed.join(', ')} — anything that depends on where the
            touchline is inherits that assumption.
          </p>
        ) : null}
        {geometry.unknown.length ? (
          <p className="adm-warn" role="status">
            No recorded pitch dimensions for {geometry.unknown.join(', ')} — the
            drawn pitch falls back to a nominal 105 x 68, which may not be theirs.
          </p>
        ) : null}
      </div>

      {sequences.length ? (
        <div className="panel">
          <h2 className="adm-primary">And then…</h2>
          {sequences.map((s, i) => (
            <div key={`s${i}`}>
              <SequenceRow
                cat={cat} step={s.step} slot={s.slot}
                onChange={(next) => editStep(s, next)}
                onMove={() => moveStep(s)}
                onRemove={() => {
                  if (s.slot === 'filter') dropFilter(s.at)
                  else setMeasures(measures.filter((_, n) => n !== s.at))
                }}
              />
              <p className="adm-note vb-slot">
                {s.slot === 'filter'
                  ? `Keeps only ${cat.subject.plural} that were followed by this.`
                  : `Keeps every ${cat.subject.label.toLowerCase()} and measures the share that were.`}
              </p>
            </div>
          ))}
        </div>
      ) : null}

      <div className="panel">
        <h2 className="adm-primary">Measure</h2>
        {measures.map((m, i) => (
          <MeasureRow
            key={m.id} cat={cat} measure={m} steps={steps}
            onChange={(next) => setMeasures(measures.map((old, n) => (n === i ? next : old)))}
            onRemove={measures.length > 1
              ? () => setMeasures(measures.filter((_, n) => n !== i))
              : undefined}
          />
        ))}
        <div className="adm-actions">
          <button type="button" className="adm-btn" onClick={addMeasure}>Add measure</button>
        </div>
      </div>

      <div className="panel">
        <h2 className="adm-primary">Break down by</h2>
        {breakdowns.map((b, i) => (
          <BreakdownRow
            key={`b${i}`} cat={cat} breakdown={b} pitch={pitch} games={games}
            onChange={(next) => setBreaks(breakdowns.map((old, n) => (n === i ? next : old)))}
            onRemove={() => setBreaks(breakdowns.filter((_, n) => n !== i))}
          />
        ))}
        <div className="adm-actions">
          <button type="button" className="adm-btn" onClick={addBreakdown}>Add breakdown</button>
        </div>
        {/*
          Two floors, one HAVING. The engine applies the highest across every
          breakdown to the combined group, so a second, lower floor does
          nothing — which is worth saying before the analyst reads a row count
          as evidence that their smaller number took effect.
        */}
        {floors.length > 1 ? (
          <p className="adm-note">
            One floor applies to the whole grouping, and the highest wins: groups
            need at least {Math.max(...floors)} {cat.subject.plural}. The lower
            figure{floors.length > 2 ? 's have' : ' has'} no effect.
          </p>
        ) : null}
      </div>

      <div className="panel">
        <h2 className="adm-primary">Show as</h2>
        <div className="adm-row vb-row">
          <select
            className="adm-select" aria-label="Visualization"
            value={spec.visualize?.as ?? ''}
            onChange={(e) => setSpec({
              ...spec, visualize: { ...spec.visualize, as: e.target.value },
            })}
          >
            {cat.visualizations.map((v: Visualization) => {
              const why = unmet(v, cat, breakdownProps)
              return (
                <option key={v.name} value={v.name} disabled={why.length > 0}>
                  {v.label}{why.length ? ` — ${why[0]}` : ''}
                </option>
              )
            })}
          </select>
          {/*
            Which measure the drawing draws. Only a table can show them all;
            everything else picks one, and the pick used to be silent — the
            first measure authored, which for a rate analysis is the count it
            was divided by rather than the rate itself.
          */}
          {drawing?.single_measure && measures.length > 1 ? (
            <>
              <span className="adm-note">showing</span>
              <select
                className="adm-select" aria-label="Measure drawn"
                value={spec.visualize?.value ?? measures[0]?.id ?? ''}
                onChange={(e) => setSpec({
                  ...spec,
                  visualize: { ...spec.visualize, as: drawing.name, value: e.target.value },
                })}
              >
                {measures.map((m) => (
                  <option key={m.id} value={m.id}>{m.label ?? m.id}</option>
                ))}
              </select>
            </>
          ) : null}
          {drawing?.description ? (
            <span className="adm-note">{drawing.description}</span>
          ) : null}
        </div>
        <div className="adm-actions">
          <button
            type="button" className="adm-btn adm-primary"
            disabled={running || games.length === 0} onClick={run}
          >
            {running ? 'Running…' : 'Run'}
          </button>
          {running ? (
            <span className="adm-note">
              First run on a match reads it cold; a few seconds is normal.
            </span>
          ) : null}
        </div>
        {error ? <p className="adm-msg adm-msg--err">{error}</p> : null}
      </div>

      {result ? <Results cat={cat} spec={spec} result={result} /> : null}

      <div className="panel">
        <h2 className="adm-primary">The analysis, as authored</h2>
        <p className="adm-note">
          This is exactly what is sent, and exactly what the engine validates.
        </p>
        <pre className="vb-spec" aria-label="AnalysisSpec">{text}</pre>
        <div className="adm-actions">
          <button
            type="button" className="adm-btn adm-btn--quiet"
            onClick={() => { void navigator.clipboard?.writeText(text) }}
          >
            Copy
          </button>
          {loaded ? (
            <span className={sameSpec(loaded, spec) ? 'adm-msg adm-msg--ok' : 'adm-note'}>
              {sameSpec(loaded, spec)
                ? 'Round-trips unchanged: loaded spec === authored spec'
                : 'Edited since loading'}
            </span>
          ) : null}
        </div>
        <details className="vb-load">
          <summary>Load a spec</summary>
          <textarea
            className="adm-input vb-paste" aria-label="Spec to load" rows={8}
            value={draft} onChange={(e) => setDraft(e.target.value)}
          />
          <div className="adm-actions">
            <button type="button" className="adm-btn" onClick={load}>Load</button>
          </div>
          {loadNote.length ? (
            <ul className="adm-msg adm-msg--err">
              {loadNote.map((r) => <li key={r}>{r}</li>)}
            </ul>
          ) : null}
        </details>
      </div>

      {cat.warnings.length ? (
        <div className="panel">
          <h2 className="adm-primary">Known caveats</h2>
          {cat.warnings.map((w) => (
            <p key={w.name} className={w.severity === 'high' ? 'adm-warn' : 'adm-note'}>
              <strong>{w.summary}</strong>{w.detail ? ` ${w.detail}` : ''}
              {w.verified ? ` (verified ${w.verified})` : ''}
            </p>
          ))}
        </div>
      ) : null}
    </>
  )
}

// ------------------------------------------------------------------ results

/**
 * The result, drawn.
 *
 * Which column is which comes from the result's own schema plus the spec that
 * asked for it: breakdown columns arrive in the order they were authored, so
 * the Nth breakdown column is the Nth breakdown property, and the axis it sits
 * on is the catalog's. Nothing is matched on a column's label.
 */
function Results(
  { cat, spec, result }: { cat: SubjectCatalog; spec: AnalysisSpec; result: AnalysisResult },
) {
  const canvas = useRef<HTMLCanvasElement | null>(null)
  /** Answers "which cell is under the pointer", rebuilt by every draw. */
  const hit = useRef<HitTest | null>(null)
  const [hover, setHover] = useState<{ cell: PitchCell; x: number; y: number } | null>(null)
  /** A clicked cell's drill-down: the breakdown values that name its group. */
  const [drill, setDrill] = useState<{ bin: Record<string, unknown>; title: string } | null>(null)
  const groups = result.columns
    .map((c, i) => ({ c, i }))
    .filter(({ c }) => c.role === 'breakdown')
  const measured = result.columns
    .map((c, i) => ({ c, i }))
    .filter(({ c }) => c.role === 'measure')

  const as = spec.visualize?.as
  const breakdowns = spec.breakdowns ?? []

  /** The breakdown column carrying a pitch axis, via the property that made it. */
  const axisAt = (axis: string): number | undefined => {
    const prop = axisProperty(cat, axis)
    if (!prop) return undefined
    const n = breakdowns.findIndex((b) => b.property === prop.name)
    return n < 0 ? undefined : groups[n]?.i
  }

  const value = useMemo(() => {
    const named = spec.visualize?.value
    if (named) {
      const hit = result.columns.findIndex((c) => c.name === named)
      if (hit >= 0) return hit
    }
    return measured[0]?.i
  }, [spec, result, measured])

  /**
   * What the bottom of the table can honestly say about each measure column.
   *
   * Adding a column up only means something when the measure is additive:
   * counts and totals are, a share or an average is not, and summing those
   * would print a number nobody asked for. The catalog says which is which —
   * this file does not decide it, and a measure authored later comes with its
   * own answer. An empty spec means the compiler's default measure, a count.
   */
  const totals = useMemo(() => {
    const fnOf = (name: string): string =>
      (spec.measures ?? []).find((m) => m.id === name)?.fn ?? 'count'
    return measured.map(({ c, i }) => {
      const nums = result.rows
        .map((r) => r[i])
        .filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
      const additive = measureBy(cat, fnOf(c.name))?.additive === true
      return { i, total: additive && nums.length ? nums.reduce((a, b) => a + b, 0) : null }
    })
  }, [cat, spec, result, measured])

  const cells = useMemo((): PitchCell[] => {
    const xi = axisAt('x'); const yi = axisAt('y')
    if (xi === undefined || yi === undefined || value === undefined) return []
    const out: PitchCell[] = []
    result.rows.forEach((row, index) => {
      const x = Number(row[xi]); const y = Number(row[yi]); const v = Number(row[value])
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(v)) return
      // The shaded measure's own value, printed as the table prints it — a
      // count stays a count, a rate keeps its decimals. `index` is what lets a
      // hover recover the whole row, measures the heatmap does not shade
      // included.
      out.push({ x, y, value: v, text: show(v, result.columns[value]?.unit), index })
    })
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, value, cat, spec])

  useEffect(() => {
    const el = canvas.current
    if (!el || as !== 'pitch_heatmap' || cells.length === 0) return
    const xi = axisAt('x'); const yi = axisAt('y')
    const px = axisProperty(cat, 'x')?.range
    const py = axisProperty(cat, 'y')?.range
    const bx = xi === undefined ? null : result.columns[xi]?.bin_width
    const by = yi === undefined ? null : result.columns[yi]?.bin_width
    if (!px || !py || !bx || !by) return
    // Draw the pitch that was played on. The catalog's range is a nominal
    // 105 x 68 and is only the fallback: it is right for four of dev's
    // twenty-eight matches, so an outline drawn from it puts the touchline in
    // the wrong place for most results. When the result spans more than one
    // geometry there is no single true outline — the mixed-pitch warning at
    // the picker already says the cells are not comparable, and the nominal is
    // as good a backdrop as any.
    const played = shapes(result.pitch, result.matches)
    const only = played.length === 1 ? played[0]?.dims : undefined
    const half = { x: only ? halfOf(only, 'length') : px.max,
                   y: only ? halfOf(only, 'width') : py.max }
    hit.current = draw(el, {
      cells, binX: bx, binY: by,
      pitchX: { min: -half.x, max: half.x }, pitchY: { min: -half.y, max: half.y },
    })
    // The old hit test belonged to the old layout; a stale tooltip would point
    // at a cell that has moved.
    setHover(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cells, as, result, cat, spec])

  const peak = useMemo(() => {
    if (value === undefined) return 0
    return result.rows.reduce((m, r) => Math.max(m, Number(r[value]) || 0), 0)
  }, [result, value])

  // Cheap enough to build every render: one pass over rows that are already in
  // memory, and no hook means no stale grid when the spec moves under it.
  const pair = as === 'matrix' ? orient(cat, breakdowns, groups) : null
  const model = pair && value !== undefined ? grid(result, pair.row, pair.col, value) : null
  /** The floor the engine applied: the highest any one breakdown asked for. */
  const floor = Math.max(0, ...breakdowns.map((b) => b.min_subjects ?? 0))

  return (
    <div className="panel">
      {/*
        The question this table answers, said in the catalog's own words and
        rebuilt from the spec every render. Not the spec's authored `question`
        field: that one is a snapshot and goes stale the moment a filter
        changes, and a heading that quietly stops matching the numbers under it
        is worse than no heading.
      */}
      <h2 className="adm-primary">{questionFor(cat, spec)}</h2>
      <p className="adm-note">
        {result.row_count} row{result.row_count === 1 ? '' : 's'} across{' '}
        {result.matches.join(', ') || 'no matches'}
        {result.truncated ? ' (truncated)' : ''}
      </p>
      {result.notes.map((n) => <p key={n} className="adm-warn">{n}</p>)}

      {as === 'pitch_heatmap' && cells.length > 0 ? (
        <div className="vb-pitch-wrap">
          <canvas
            ref={canvas} className="vb-pitch" aria-label="Pitch heatmap"
            onMouseMove={(e) => {
              const cell = hit.current?.(e.nativeEvent.offsetX, e.nativeEvent.offsetY) ?? null
              setHover(cell ? { cell, x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY } : null)
            }}
            onMouseLeave={() => setHover(null)}
            onClick={(e) => {
              // A cell names its group by its row's breakdown values; the
              // examples endpoint turns those back into the raw rows the
              // aggregate counted. Keyed by result column name so a spec
              // with more breakdowns than the two axes still pins them all.
              const cell = hit.current?.(e.nativeEvent.offsetX, e.nativeEvent.offsetY) ?? null
              const row = cell?.index === undefined ? undefined : result.rows[cell.index]
              if (!row) return
              const bin: Record<string, unknown> = {}
              for (const { c, i } of groups) bin[c.name] = row[i]
              const title = groups.map(({ c, i }) => {
                const lo = Number(row[i]); const w = c.bin_width
                return w && Number.isFinite(lo)
                  ? `${c.label} ${show(lo, c.unit)} to ${show(lo + w, c.unit)}${c.unit === 'm' ? 'm' : ''}`
                  : `${c.label} ${show(row[i], c.unit)}`
              }).join(' \u00b7 ')
              setDrill({ bin, title })
            }}
          />
          {/*
            Everything the row holds, not just the shaded measure: a cell that
            reads "12" is a different fact if it is 12 of 14 than if it is 12 of
            300, and the second measure is usually where that lives.
          */}
          {hover ? (
            <div
              className="vb-tip" role="tooltip"
              style={{ left: `${hover.x}px`, top: `${hover.y}px` }}
            >
              {groups.map(({ c, i }) => {
                const lo = Number(result.rows[hover.cell.index ?? -1]?.[i])
                const w = c.bin_width
                return (
                  <div key={c.name} className="vb-tip__row">
                    <span>{c.label}</span>
                    <strong>
                      {w && Number.isFinite(lo)
                        ? `${show(lo, c.unit)} to ${show(lo + w, c.unit)}${c.unit === 'm' ? 'm' : ''}`
                        : show(result.rows[hover.cell.index ?? -1]?.[i], c.unit)}
                    </strong>
                  </div>
                )
              })}
              {measured.map(({ c, i }) => (
                <div key={c.name} className="vb-tip__row">
                  <span>{c.label}</span>
                  <strong>{show(result.rows[hover.cell.index ?? -1]?.[i], c.unit)}</strong>
                </div>
              ))}
              {peak > 0 ? (
                <div className="vb-tip__row vb-tip__foot">
                  <span>Share of busiest cell</span>
                  <strong>{`${((hover.cell.value / peak) * 100).toFixed(0)}%`}</strong>
                </div>
              ) : null}
              <div className="vb-tip__row vb-tip__foot">
                <span>Click for the rows behind this cell</span>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {drill ? (
        <ExamplesModal
          spec={spec} bin={drill.bin} title={drill.title}
          onClose={() => setDrill(null)}
        />
      ) : null}

      {model ? (
        <Matrix
          cat={cat} model={model} result={result} measured={measured}
          floor={floor || undefined}
        />
      ) : null}

      {as === 'bar' && value !== undefined && groups[0] ? (
        <div className="vb-bars">
          {result.rows.map((row, i) => {
            const g = groups[0]
            const v = Number(row[value]) || 0
            return (
              <div key={i} className="vb-bar">
                <span className="vb-bar__label">{show(g ? row[g.i] : null)}</span>
                <span className="vb-bar__track">
                  <span
                    className="vb-bar__fill"
                    style={{ width: `${peak ? (v / peak) * 100 : 0}%` }}
                  />
                </span>
                <span className="vb-bar__value">
                  {show(v, result.columns[value]?.unit)}
                </span>
              </div>
            )
          })}
        </div>
      ) : null}

      <table className="adm-table">
        <thead>
          <tr>{result.columns.map((c) => (
            <th key={c.name} className={c.role === 'measure' ? 'adm-num' : undefined}>
              {c.label}{c.bin_width ? ` (${c.bin_width}${c.unit ?? ''} bins)` : ''}
            </th>
          ))}</tr>
        </thead>
        <tbody>
          {result.rows.map((row, i) => (
            <tr key={i}>
              {result.columns.map((c, j) => (
                <td key={c.name} className={c.role === 'measure' ? 'adm-num' : undefined}>
                  {show(row[j], c.unit)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {result.rows.length && totals.length ? (
          <tfoot>
            <tr>
              {result.columns.map((c, j) => {
                const t = totals.find((x) => x.i === j)
                if (!t) return <th key={c.name} scope="row">{j === 0 ? 'Total' : ''}</th>
                return (
                  <td
                    key={c.name}
                    className="adm-num"
                    title={t.total === null ? `${c.label} cannot be added up` : undefined}
                  >
                    {t.total === null ? '—' : show(t.total, c.unit)}
                  </td>
                )
              })}
            </tr>
          </tfoot>
        ) : null}
      </table>
      {result.rows.length && totals.some((t) => t.total === null) ? (
        <p className="adm-note">
          A dash means adding that measure up would not mean anything.
        </p>
      ) : null}
      {result.rows.length === 0 ? <p className="adm-empty">No rows.</p> : null}
    </div>
  )
}
